/**
 * TODO Task Form
 *
 * Create/edit modal for a single TODO task. Mirrors the server-side validation
 * (title, description, priority, status, deadline) client-side so the user gets
 * immediate, non-destructive field-level feedback; the server remains the
 * source of truth. Submit is disabled while the form is invalid or a request is
 * in flight.
 *
 * In `create` mode the form collects title, description, priority, status, and
 * deadline and calls `todoApi.createTask` (via `onSubmit`). In `edit` mode the
 * form is prefilled from `initialTask` and edits title, description, priority,
 * and deadline (status is managed separately from the task card), calling
 * `todoApi.editTask`. On a failed submission the form stays open and surfaces an
 * inline error while preserving the entered values.
 *
 * Props:
 *   - mode:        'create' | 'edit'
 *   - initialTask: task object to prefill in edit mode
 *   - onSubmit:    async (payload) => void; throws on failure
 *   - onClose:     () => void; called to dismiss the form (cancel or success)
 *
 * Requirements: 3.1, 3.4, 3.5, 3.6, 3.7, 3.8, 4.1, 4.4, 4.5, 4.6, 4.7
 */

import React, { useMemo, useState } from 'react';
import './TodoTaskForm.css';

// Client-side mirrors of the server enums (todo_api_handler.py).
export const PRIORITIES = ['Low', 'Medium', 'High'];
export const STATUSES = ['Completed', 'In Progress', 'Backlog'];

// Server validation bounds.
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 2000;
const DEFAULT_STATUS = 'Backlog'; // Req 3.4: default status on create.
const DEFAULT_PRIORITY = 'Medium';

/**
 * Validate an optional ISO-8601 calendar date (YYYY-MM-DD). Returns true when
 * the value is empty (deadline is optional) or a real calendar date, and false
 * for malformed strings or impossible dates such as 2024-02-30.
 */
export function isValidDeadline(raw) {
  if (raw === '' || raw === null || raw === undefined) {
    return true;
  }
  if (typeof raw !== 'string') {
    return false;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  // Round-trip through Date to reject impossible calendar dates.
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * Compute field-level validation errors for the current form values, mirroring
 * the server rules. Returns an object keyed by field name; an empty object means
 * the form is valid.
 */
export function validateForm(values, mode) {
  const errors = {};

  const trimmedTitle = (values.title || '').trim();
  if (trimmedTitle.length === 0) {
    errors.title = 'Title is required.';
  } else if (trimmedTitle.length > TITLE_MAX) {
    errors.title = `Title must be at most ${TITLE_MAX} characters.`;
  }

  const description = values.description || '';
  if (description.length > DESCRIPTION_MAX) {
    errors.description = `Description must be at most ${DESCRIPTION_MAX} characters.`;
  }

  if (!PRIORITIES.includes(values.priority)) {
    errors.priority = 'Select a valid priority.';
  }

  // Status is only submitted in create mode; edit changes status separately.
  if (mode === 'create' && !STATUSES.includes(values.status)) {
    errors.status = 'Select a valid status.';
  }

  if (!isValidDeadline(values.deadline)) {
    errors.deadline = 'Enter a valid date (YYYY-MM-DD).';
  }

  return errors;
}

const TodoTaskForm = ({ mode = 'create', initialTask = null, onSubmit, onClose }) => {
  const isEdit = mode === 'edit';

  const [values, setValues] = useState(() => ({
    title: initialTask?.title || '',
    description: initialTask?.description || '',
    priority: initialTask?.priority || DEFAULT_PRIORITY,
    status: initialTask?.status || DEFAULT_STATUS,
    deadline: initialTask?.deadline || ''
  }));
  // Track which fields have been touched so errors only show after interaction.
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  // Reveal all errors once a submit has been attempted.
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => validateForm(values, mode), [values, mode]);
  const isValid = Object.keys(errors).length === 0;

  const setField = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
    setServerError('');
  };

  const markTouched = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const showError = (field) => (touched[field] || submitted) && errors[field];

  const buildPayload = () => {
    const payload = {
      title: values.title.trim(),
      description: values.description || '',
      priority: values.priority,
      // Optional deadline: send null when blank so the server clears it.
      deadline: values.deadline ? values.deadline : null
    };
    // Status is only part of the create payload; edit keeps the existing status.
    if (!isEdit) {
      payload.status = values.status;
    }
    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!isValid || submitting) {
      return;
    }
    setSubmitting(true);
    setServerError('');
    try {
      await onSubmit(buildPayload());
      // On success the parent refreshes the list; dismiss the form.
      if (onClose) {
        onClose();
      }
    } catch (e) {
      // Non-destructive: keep the form open with the entered values intact.
      setServerError(
        isEdit
          ? 'Could not save your changes. Please try again.'
          : 'Could not create the task. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = () => {
    if (!submitting && onClose) {
      onClose();
    }
  };

  return (
    <div className="todo-form-overlay" onClick={handleOverlayClick}>
      <div
        className="todo-form-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit task' : 'Create task'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="todo-form__header">
          <h3 className="todo-form__title">{isEdit ? 'Edit Task' : 'New Task'}</h3>
          <button
            type="button"
            className="todo-form__close"
            aria-label="Close"
            disabled={submitting}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form className="todo-form__body" onSubmit={handleSubmit} noValidate>
          <label className="todo-form__field">
            <span className="todo-form__label">Title</span>
            <input
              type="text"
              className="todo-form__input"
              value={values.title}
              maxLength={TITLE_MAX + 1}
              disabled={submitting}
              onChange={setField('title')}
              onBlur={markTouched('title')}
              aria-invalid={showError('title') ? 'true' : 'false'}
            />
            {showError('title') && (
              <span className="todo-form__error" role="alert">
                {errors.title}
              </span>
            )}
          </label>

          <label className="todo-form__field">
            <span className="todo-form__label">Description</span>
            <textarea
              className="todo-form__textarea"
              value={values.description}
              rows={4}
              disabled={submitting}
              onChange={setField('description')}
              onBlur={markTouched('description')}
              aria-invalid={showError('description') ? 'true' : 'false'}
            />
            {showError('description') && (
              <span className="todo-form__error" role="alert">
                {errors.description}
              </span>
            )}
          </label>

          <div className="todo-form__row">
            <label className="todo-form__field">
              <span className="todo-form__label">Priority</span>
              <select
                className="todo-form__input"
                value={values.priority}
                disabled={submitting}
                onChange={setField('priority')}
                onBlur={markTouched('priority')}
                aria-invalid={showError('priority') ? 'true' : 'false'}
              >
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
              {showError('priority') && (
                <span className="todo-form__error" role="alert">
                  {errors.priority}
                </span>
              )}
            </label>

            {!isEdit && (
              <label className="todo-form__field">
                <span className="todo-form__label">Status</span>
                <select
                  className="todo-form__input"
                  value={values.status}
                  disabled={submitting}
                  onChange={setField('status')}
                  onBlur={markTouched('status')}
                  aria-invalid={showError('status') ? 'true' : 'false'}
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                {showError('status') && (
                  <span className="todo-form__error" role="alert">
                    {errors.status}
                  </span>
                )}
              </label>
            )}

            <label className="todo-form__field">
              <span className="todo-form__label">Deadline</span>
              <input
                type="date"
                className="todo-form__input"
                value={values.deadline || ''}
                disabled={submitting}
                onChange={setField('deadline')}
                onBlur={markTouched('deadline')}
                aria-invalid={showError('deadline') ? 'true' : 'false'}
              />
              {showError('deadline') && (
                <span className="todo-form__error" role="alert">
                  {errors.deadline}
                </span>
              )}
            </label>
          </div>

          {serverError && (
            <div className="todo-form__server-error" role="alert">
              {serverError}
            </div>
          )}

          <div className="todo-form__actions">
            <button
              type="button"
              className="todo-form__btn todo-form__btn--secondary"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="todo-form__btn todo-form__btn--primary"
              disabled={!isValid || submitting}
            >
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TodoTaskForm;
